import { useState, useEffect, useRef } from 'react';
import {
  Clock,
  ChevronRight,
  CheckCircle2,
  Circle,
  Loader2,
  Square,
  Sparkles,
  Users,
  FileText,
  AlertCircle,
  Download,
  Copy,
  Check,
} from 'lucide-react';
import type { MeetingRecording } from '../../lib/types/meetingRecording';
import { MeetingAudioPlayer } from './MeetingAudioPlayer';
import { CHIP_DONE_CLS, CHIP_LATE_CLS, CHIP_NEUTRAL_CLS, mediaTileCls } from '../shared/timeline/cardStyles';
import { CardMedia } from '../shared/timeline/CardMedia';
import { TimelineFooter } from '../shared/timeline/TimelineFooter';
import { useMeetingRecordingStore } from '../../lib/stores/meetingRecordingStore';

interface MeetingRecordingCardProps {
  recording: MeetingRecording;
  onStopRecording?: (id: string) => void;
  /**
   * true nelle cronologie lead/offerta, dove la card sta sotto una chip giorno:
   * l'header mostra SOLO l'ora e la data completa resta nel tooltip (B7).
   * Fuori dalle cronologie (lista registrazioni, pannello cliente dell'offerta)
   * la data completa serve ancora: default false.
   */
  compactDate?: boolean;
}

const platformLabels: Record<string, string> = {
  google_meet: 'Google Meet',
  teams: 'Microsoft Teams',
  zoom: 'Zoom',
};

const statusConfig: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  pending: { label: 'In attesa', color: 'text-yellow-600 bg-yellow-500/10', icon: <Clock className="w-3 h-3" /> },
  joining: { label: 'In accesso', color: 'text-blue-600 bg-blue-500/10', icon: <Loader2 className="w-3 h-3 animate-spin" /> },
  active: { label: 'Registrando', color: 'text-red-600 bg-red-500/10', icon: <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /> },
  completed: { label: 'Completato', color: 'text-green-600 bg-green-500/10', icon: <CheckCircle2 className="w-3 h-3" /> },
  failed: { label: 'Errore', color: 'text-red-600 bg-red-500/10', icon: <AlertCircle className="w-3 h-3" /> },
  stopped: { label: 'Fermato', color: 'text-gray-600 bg-gray-500/10', icon: <Square className="w-3 h-3" /> },
};

// Niente emoji nella UI: il tono lo dice un pallino colorato accanto all'etichetta.
const sentimentConfig: Record<string, { label: string; dot: string }> = {
  positive: { label: 'Positivo', dot: 'bg-[#0F7B6C]' },
  neutral: { label: 'Neutrale', dot: 'bg-[#9B9A97]' },
  negative: { label: 'Negativo', dot: 'bg-[#D44C47]' },
};

function formatDuration(seconds: number | null): string {
  if (!seconds) return '--';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  if (hrs > 0) return `${hrs}h ${mins}m`;
  return `${mins}m`;
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function MeetingRecordingCard({ recording, onStopRecording, compactDate = false }: MeetingRecordingCardProps) {
  const [expandedSection, setExpandedSection] = useState<'summary' | 'transcript' | null>(null);
  const { generateSummary } = useMeetingRecordingStore();
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [copiedSummary, setCopiedSummary] = useState(false);
  const [copiedTranscript, setCopiedTranscript] = useState(false);

  const status = statusConfig[recording.status] || statusConfig.pending;
  const hasTranscript = !!recording.full_transcript;
  const hasSummary = !!recording.ai_summary;

  const handleGenerateSummary = async () => {
    setGeneratingSummary(true);
    await generateSummary(recording.id);
    setGeneratingSummary(false);
  };

  const toggleSection = (section: 'summary' | 'transcript') => {
    setExpandedSection(prev => prev === section ? null : section);
  };

  const handleCopySummary = async (e: React.MouseEvent) => {
    e.stopPropagation();
    let text = recording.ai_summary || '';
    if ((recording.ai_key_points || []).length > 0) {
      text += '\n\nPunti chiave:\n' + recording.ai_key_points.map(p => `• ${p}`).join('\n');
    }
    if ((recording.ai_action_items || []).length > 0) {
      text += '\n\nCose da fare:\n' + recording.ai_action_items.map(a => `${a.completed ? '[x]' : '[ ]'} ${a.text}${a.assignee ? ` (${a.assignee})` : ''}`).join('\n');
    }
    await navigator.clipboard.writeText(text);
    setCopiedSummary(true);
    setTimeout(() => setCopiedSummary(false), 2000);
  };

  const handleCopyTranscript = async (e: React.MouseEvent) => {
    e.stopPropagation();
    let text = '';
    if ((recording.transcript_segments || []).length > 0) {
      text = recording.transcript_segments.map(s => {
        const sec = Number(s.start_time) || 0;
        const ts = `${Math.floor(sec / 60)}:${String(Math.floor(sec % 60)).padStart(2, '0')}`;
        return `${s.speaker} [${ts}]: ${s.text}`;
      }).join('\n');
    } else {
      text = recording.full_transcript || '';
    }
    await navigator.clipboard.writeText(text);
    setCopiedTranscript(true);
    setTimeout(() => setCopiedTranscript(false), 2000);
  };

  return (
    // Card flat allineata alla cronologia: radius 8, bordo palette Notion, nessuna ombra
    <div className="border border-[rgba(55,53,47,0.16)] dark:border-[#3F4447] hover:border-[rgba(55,53,47,0.26)] dark:hover:border-[#565D61] transition-colors rounded-lg bg-background overflow-hidden">
      {/* Testa: stessa anatomia delle altre card della cronologia */}
      <div className="p-3">
        <CardMedia
          media={
            <span className={mediaTileCls('flusia')}>
              <img src="/icona.png" alt="Flusia" className="h-5 w-5 rounded-full object-contain" />
            </span>
          }
        >
          <h4 className="truncate text-sm font-medium text-[#37352F] dark:text-white">
            {recording.meeting_title || platformLabels[recording.platform] || 'Meeting'}
          </h4>
          <div className="mt-0.5 flex items-center gap-3 text-xs text-[#787774] dark:text-white/50">
            {!compactDate && <span>{formatDate(recording.created_at)}</span>}
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatDuration(recording.duration_seconds)}
            </span>
            {(recording.participants || []).length > 0 && (
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3" />
                {recording.participants.length}
              </span>
            )}
          </div>
        </CardMedia>
      </div>

      {/* Audio player */}
      {recording.recording_url && recording.status === 'completed' && (
        <div className="px-3 pb-3">
          <MeetingAudioPlayer src={recording.recording_url} />
        </div>
      )}

      {/* AI Summary - preview always visible */}
      {hasSummary && (
        <div className="border-t border-border px-3 py-3 space-y-2">
          <div className="flex items-center gap-1.5 mb-1">
            <Sparkles className="w-3 h-3 text-purple-500" />
            <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Riassunto IA</span>
            <button
              onClick={handleCopySummary}
              className="ml-auto p-1 rounded hover:bg-accent/50 text-muted-foreground hover:text-foreground transition-colors"
              title="Copia riassunto"
            >
              {copiedSummary ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
            </button>
          </div>

          {/* Summary text - truncated or full */}
          <p className={`text-xs text-foreground/80 leading-relaxed whitespace-pre-line ${expandedSection !== 'summary' ? 'line-clamp-3' : ''}`}>
            {recording.ai_summary}
          </p>

          {/* Expanded: key points + action items */}
          {expandedSection === 'summary' && (
            <div className="space-y-3 pt-1">
              {(recording.ai_key_points || []).length > 0 && (
                <div>
                  <h5 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Punti chiave</h5>
                  <ul className="space-y-1">
                    {(recording.ai_key_points || []).map((point, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-xs text-foreground/80">
                        <span className="text-purple-500 mt-0.5">•</span>
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {(recording.ai_action_items || []).length > 0 && (
                <div>
                  <h5 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Cose da fare</h5>
                  <ul className="space-y-1.5">
                    {(recording.ai_action_items || []).map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs">
                        {item.completed ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                        ) : (
                          <Circle className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0 mt-0.5" />
                        )}
                        <span className={item.completed ? 'line-through text-muted-foreground' : 'text-foreground/80'}>
                          {item.text}
                          {item.assignee && <span className="text-muted-foreground ml-1">({item.assignee})</span>}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <button
            onClick={() => toggleSection('summary')}
            aria-expanded={expandedSection === 'summary'}
            className="text-[10px] font-medium text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 transition-colors"
          >
            {expandedSection === 'summary' ? 'Mostra meno' : 'Mostra tutto'}
          </button>
        </div>
      )}

      {/* Generate summary button (if transcript available but no summary yet) */}
      {hasTranscript && !hasSummary && !recording.ai_processed_at && recording.status === 'completed' && (
        <div className="border-t border-border px-3 py-2.5">
          <button
            onClick={handleGenerateSummary}
            disabled={generatingSummary}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 transition-colors"
          >
            {generatingSummary ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Sparkles className="w-3.5 h-3.5" />
            )}
            {generatingSummary ? 'Genero il riassunto…' : 'Genera riassunto IA'}
          </button>
        </div>
      )}

      {/* Transcript section - Notion-like toggle */}
      {hasTranscript && (
        <div className="border-t border-border">
          <div className="flex items-center px-3 py-2 hover:bg-accent/30 transition-colors">
            <button
              onClick={() => toggleSection('transcript')}
              aria-expanded={expandedSection === 'transcript'}
              className="flex items-center gap-1 flex-1"
            >
              <ChevronRight className={`w-3.5 h-3.5 text-muted-foreground transition-transform duration-200 ${expandedSection === 'transcript' ? 'rotate-90' : ''}`} />
              <FileText className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Trascrizione</span>
            </button>
            <button
              onClick={handleCopyTranscript}
              className="p-1 rounded hover:bg-accent/50 text-muted-foreground hover:text-foreground transition-colors"
              title="Copia trascrizione"
            >
              {copiedTranscript ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
            </button>
          </div>

          {expandedSection === 'transcript' && (
            <div className="px-3 pb-4 max-h-80 overflow-y-auto">
              {(recording.transcript_segments || []).length > 0 ? (
                <div className="space-y-2">
                  {(recording.transcript_segments || []).map((segment, i) => {
                    const startSec = Number(segment.start_time) || 0;
                    return (
                      <div
                        key={i}
                        role="button"
                        tabIndex={0}
                        className="group cursor-pointer hover:bg-accent/30 rounded px-2 py-1 -mx-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2383E2]/40"
                        onClick={() => {
                          const seekTo = (window as any).__meetingAudioSeekTo;
                          if (seekTo) seekTo(startSec);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            const seekTo = (window as any).__meetingAudioSeekTo;
                            if (seekTo) seekTo(startSec);
                          }
                        }}
                      >
                        <div className="flex items-baseline gap-2">
                          <span className="text-[10px] font-semibold text-blue-600 dark:text-blue-400">
                            {segment.speaker}
                          </span>
                          <span className="text-[9px] text-muted-foreground">
                            {Math.floor(startSec / 60)}:{String(Math.floor(startSec % 60)).padStart(2, '0')}
                          </span>
                        </div>
                        <p className="text-xs text-foreground/80 leading-relaxed">{segment.text}</p>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-foreground/80 whitespace-pre-line leading-relaxed">
                  {recording.full_transcript}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      <div className="px-3 pb-3">
        <TimelineFooter
          tipo="Registrazione"
          accent="flusia"
          date={recording.created_at}
          titoloData={formatDate(recording.created_at)}
          left={
            <>
              <span className={recording.status === 'completed' ? CHIP_DONE_CLS : recording.status === 'failed' ? CHIP_LATE_CLS : CHIP_NEUTRAL_CLS}>
                {status.icon}
                {status.label}
              </span>
              {recording.ai_sentiment && sentimentConfig[recording.ai_sentiment] && (
                <span className={CHIP_NEUTRAL_CLS}>
                  <span className={`h-1.5 w-1.5 rounded-full ${sentimentConfig[recording.ai_sentiment].dot}`} />
                  {sentimentConfig[recording.ai_sentiment].label}
                </span>
              )}
            </>
          }
        />
      </div>
    </div>
  );
}
